import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ConversationTurn.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ConversationTurnProps {
  /**
   * The user half — a `UserMessage`, or a bubble in its own right-aligning rail.
   *
   * Optional because a turn can open on the agent: `Task reports back` begins with the task card
   * and has no bubble at all. When it is absent the 6px row is not rendered, so the agent
   * side lands exactly where the column's 12px gap puts it rather than 6px lower.
   *
   * **The turn owns the 6px block padding around this slot** (INVENTORY §1.45), and enforces it:
   * the stylesheet zeroes `padding-block` on whatever lands here, so a `UserMessage` — which carries
   * the same 6px on its own row, with no prop to suppress it — composes to 6, not 12.
   *
   * The consequence is that the node here must be a padding-owning **row**: a `UserMessage`, or a
   * bubble already inside its `align-items: flex-end` rail, which is the only child conversation.md
   * draws under a UserRow. A bare `UserBubble` would lose the block half of its own 11/13 padding.
   */
  user?: React.ReactNode;
  /**
   * The agent half — any mix of `ActivityBlock`, `AssistantProse`, `ArtifactCard`, `TaskCard`,
   * `AnswerChipRow`, `OrderedList`, `DataTable`, `RegionReference`, `SupersededQuote` and a
   * `MessageActions side="assistant"`, 12px apart in the order the reply happened.
   *
   * Optional for the mirror of the case above: `Many screenshots` ends on the user side with no
   * reply yet.
   *
   * Nesting is content-driven and belongs to the children, not here. `Agent/Done` is the case to
   * hold on to — its paragraph, its artifact card and its action row are three DIRECT children at
   * 12px, not a group: an `ActivityBlock`'s 8px wrapper exists only to bind a status row to the
   * body it is a heading for, and Done has no status row to bind. Wrapping a finished answer in one
   * anyway would tighten three unrelated blocks to 8 and claim a relationship the design does not.
   */
  children?: React.ReactNode;
  /**
   * The 16px above the turn, which every turn carries except the first.
   *
   * Defaults **on**: a turn's resting state is "somewhere in a thread", and a thread has exactly
   * one turn that is not. `Founder · cold start` opens on a `DayDivider`, so the first turn cannot
   * be found with `:first-child` from CSS — the caller is the only one who knows which turn is
   * first, so it says so with `spaced={false}`. `Manager · one shot` is the one-turn thread and
   * passes it too.
   */
  spaced?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * One exchange — the user's message, then everything the agent did about it.
 *
 * It draws nothing. No ground, no rule, no box, no avatar, no timestamp: a turn is a *rhythm*, not
 * a container. Its children sit 12px apart like every other sibling in the column, and the next
 * turn starts 28px away (the column's 12 plus this component's 16). That 12-against-28 is the
 * entire grouping signal in the design, and it holds across all four conversation artboards
 * without a single divider.
 *
 * The two halves each take 6px of block padding rather than sharing a 12px gap, which is what keeps
 * a turn honest when a half is missing — see the note in the stylesheet. It is also why a turn
 * and a loose stack of blocks (the eight `Agent` boards, which have no turn wrapper at all)
 * measure the same on screen: 6 + 6 is 12, and 12 is what the column would have given them anyway.
 */
export function ConversationTurn({
  user,
  children,
  spaced = true,
  theme: themeProp,
  className,
}: ConversationTurnProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.turn, spaced && styles.spaced, className)}>
      {/* Both halves are conditional, and that is load-bearing rather than defensive: an empty
          `.user` would still paint 12px of padding and push the reply down a row that has nothing
          in it. `Task reports back` (no bubble) and `Many screenshots` (no reply) are the two
          artboards that prove each side, and they are why this is not a single always-rendered
          pair of divs. */}
      {user ? <div className={styles.user}>{user}</div> : null}
      {children ? <div className={styles.assistant}>{children}</div> : null}
    </div>
  );
}
